import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2, CalendarClock, Filter } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  expire: { label: "EXPIRÉ", cls: "badge-alert" },
  bientot: { label: "BIENTÔT (<30j)", cls: "badge-warn" },
  proche: { label: "PROCHE (<90j)", cls: "badge-info" },
  actif: { label: "ACTIF", cls: "badge-success" },
};

export default function Batches() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all"); // all|expire|bientot|proche|actif
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ product_id: "", site_id: "", batch_number: "", expiry_date: "", quantity: "", note: "" });

  const load = async () => {
    const [b, p, s] = await Promise.all([api.get("/batches"), api.get("/products"), api.get("/sites")]);
    setBatches(b.data); setProducts(p.data); setSites(s.data);
    if (!form.site_id && s.data.length === 1) setForm((f) => ({ ...f, site_id: s.data[0].id }));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filterStatus === "all") return batches;
    return batches.filter((b) => b.status === filterStatus);
  }, [batches, filterStatus]);

  const counts = useMemo(() => {
    const c = { all: batches.length, expire: 0, bientot: 0, proche: 0, actif: 0 };
    for (const b of batches) c[b.status] = (c[b.status] || 0) + 1;
    return c;
  }, [batches]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/batches", { ...form, quantity: Number(form.quantity) });
      toast.success("Lot créé · stock mis à jour");
      setShowForm(false);
      setForm({ product_id: "", site_id: sites[0]?.id || "", batch_number: "", expiry_date: "", quantity: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer le lot "${b.batch_number}" ? Le stock sera diminué de ${b.quantity}.`)) return;
    try {
      await api.delete(`/batches/${b.id}`);
      toast.success("Lot supprimé · stock ajusté");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const todayPlus = (n) => {
    const d = new Date(); d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lots & Péremption"
        subtitle="Traçabilité · Dates de péremption"
        testId="batches-header"
        actions={canEdit && (
          <button data-testid="new-batch-btn" className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
            <Plus size={14}/> Nouveau lot
          </button>
        )}
      />

      <div className="surface p-4 flex gap-3 items-start">
        <CalendarClock size={16} className="text-[var(--primary,#C2569B)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--text-muted)] leading-relaxed">
          Un <b>lot</b> regroupe une quantité d'un produit avec un <b>numéro de lot</b> et une <b>date de péremption</b>.
          Créer un lot ajoute automatiquement la quantité à votre <b>stock actuel</b>. Les lots expirés ou bientôt périmés
          sont signalés ci-dessous et sur le tableau de bord.
        </div>
      </div>

      {showForm && canEdit && (
        <Surface title="Nouveau lot" testId="new-batch-form">
          <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="label-mono block mb-1.5">Produit</label>
              <select data-testid="batch-product" required className="input-base" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.sku}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Site</label>
              <select data-testid="batch-site" required className="input-base" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">N° de lot</label>
              <input data-testid="batch-number" required className="input-base" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="ex. LOT-2026-A12" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Date de péremption</label>
              <input data-testid="batch-expiry" type="date" required min={todayPlus(0)} className="input-base" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Quantité</label>
              <input data-testid="batch-qty" type="number" min="1" required className="input-base" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="ex. 50" />
            </div>
            <div className="md:col-span-3">
              <label className="label-mono block mb-1.5">Note (optionnel)</label>
              <input className="input-base" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="ex. fournisseur LABOREX, facture #1234" />
            </div>
            <div className="md:col-span-3 flex gap-2 pt-1">
              <button data-testid="submit-batch" type="submit" className="btn-primary">Créer le lot</button>
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Annuler</button>
            </div>
          </form>
        </Surface>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-[var(--text-muted)]" />
        {["all", "expire", "bientot", "proche", "actif"].map((s) => {
          const lbl = s === "all" ? "Tous" : STATUS_LABEL[s].label;
          const active = filterStatus === s;
          return (
            <button
              key={s}
              data-testid={`filter-${s}`}
              type="button"
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]"
                  : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"
              }`}
            >
              {lbl} <span className="opacity-70 ml-1">({counts[s] || 0})</span>
            </button>
          );
        })}
      </div>

      <Surface testId="batches-table">
        {filtered.length === 0 ? <Empty message="Aucun lot" /> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Produit</th><th>Site</th><th>N° Lot</th>
                <th className="text-right">Qté</th>
                <th>Péremption</th>
                <th className="text-right">Jours restants</th>
                <th>Statut</th>
                {canEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const st = STATUS_LABEL[b.status] || STATUS_LABEL.actif;
                return (
                  <tr key={b.id} data-testid={`batch-row-${b.id}`}>
                    <td>
                      <div className="font-medium">{b.product_name}</div>
                      <div className="font-data text-xs text-[var(--text-muted)]">{b.product_sku}</div>
                    </td>
                    <td className="text-[var(--text-muted)]">{b.site_name}</td>
                    <td className="font-data">{b.batch_number}</td>
                    <td className="font-data text-right">{b.quantity}</td>
                    <td className="font-data text-[var(--text-muted)]">{b.expiry_date || "—"}</td>
                    <td className={`font-data text-right ${b.days_left == null ? "text-[var(--text-muted)]" : b.days_left < 0 ? "text-[var(--alert,#c32a2a)] font-bold" : b.days_left <= 30 ? "text-[#B45309] font-semibold" : ""}`}>
                      {b.days_left == null ? "—" : b.days_left < 0 ? `${Math.abs(b.days_left)}j passés` : `${b.days_left}j`}
                    </td>
                    <td><span className={st.cls}>{st.label}</span></td>
                    {canEdit && (
                      <td>
                        <button data-testid={`del-batch-${b.id}`} onClick={() => remove(b)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)] transition-colors" title="Supprimer (réajuste le stock)">
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
